import { COLLABORATIVE_NOTE_CHANGED_EVENT } from "@core/api/encounterCollaborativeNoteApi"
import {
  CRM_SETTINGS_CHANGED_EVENT,
  CRM_STAGES_CHANGED_EVENT,
  CRM_SESSIONS_CHANGED_EVENT,
  CRM_CONSULTATIONS_CHANGED_EVENT,
  CRM_TASKS_CHANGED_EVENT,
  CRM_TIMELINE_CHANGED_EVENT,
} from "@modules/crm/crmEvents"
import { receiveTreatmentRoomDelta } from "@core/api/treatmentRoomApi"
import { PATIENT_CLASSIFICATION_CHANGED_EVENT } from "@core/api/patientClassificationApi"
import type { TreatmentRoomSyncPayload } from "@core/utils/treatmentRoomSync"
// src/utils/referenceDataUpdater.ts
import store, { RootState } from "@core/store"
import { setBroadcast } from "@/features/broadcast/broadcastSlice"
import { fetchPatient } from "@core/api/patientApi"
import { WAITING_ORDER_CHANGED_EVENT } from "@core/api/visitApi"
import { clearSelectedPatient, patchSelectedPatient } from "@/features/patient/patientSlice"
import { fetchChunaPendingCount } from "@core/api/encounterChunaApi"
import { fetchHerbalPendingCount } from "@core/api/encounterHerbalApi"
import { fetchNhisChupyakPendingCount } from "@core/api/chupyakApi"
import { fetchPublicHospitalInfo } from "@core/api/hospitalApi"
import { fetchHospitalSettings } from "@core/api/hospitalSettingApi"
import { fetchUninsuredItems, fetchUninsuredCategory } from "@core/api/uninsuredApi"
import { fetchPublicStamp, fetchMyStamp } from "@/features/stamp/stampSlice"
import { clearSelectedVisit } from "@/features/selectedVisit/selectedVisitSlice"
import { clearSelectedVisitUuid } from "@/features/visit/visitSelectionSlice"
import { resolveCurrentMemberUuidFromState } from "@core/utils/currentMemberContext"
import { getUserFeatureSettings, playGeneralReceptionAlarm, playKioskReceptionAlarm } from "@core/utils/userFeatureSettings"
import { isLocalEncounterHistoryEvent } from "@core/api/encounterHistoryApi"
import { dispatchPackageBroadcastEvent } from "@core/utils/packageMutationEvent"
import { KAKAO_LOCAL_CACHE_CHANGED_EVENT } from "@shared/components/modals/message/kakao/useKakaoLocalCacheRevision"
import {
  CID_CALL_STATUS_CHANGED_EVENT,
  CID_INCOMING_CALL_EVENT,
  CID_INCOMING_MESSAGE_EVENT,
  type CidCallStatus,
  type CidCallStatusChangedEventDetail,
  type CidIncomingCallEventDetail,
  type CidIncomingMessageEventDetail,
} from "@shared/constants/uiEvents"

//소켓 데이터 브로드캐스팅 처리
export const handleSioUpdate = async (data: {
  actionType: string
  resourcePath?: string
  event_id?: string
  eventId?: string
  patient_uuid?: string
  patientUuid?: string
  encounter_uuid?: string
  encounterUuid?: string
  visitUuid?: string
  insuranceUuid?: string
  patientIdentity?: string
  patientName?: string
  hospitalCode?: string
  visitDate?: string
  referenceDate?: string
  success?: boolean | null
  source?: string
  jobStatus?: string
  skippedReason?: string
  patientVerification?: boolean
  receptionSource?: "kiosk" | "general" | string
  reception_source?: "kiosk" | "general" | string
  cidHistoryUuid?: string
  cid_history_uuid?: string
  provider?: "kt" | "lguplus"
  remoteNumber?: string
  remote_number?: string
  eventDt?: string | null
  event_dt?: string | null
  patientId?: number | null
  patient_id?: number | null
  patientGender?: string | null
  patient_gender?: string | null
  patientAge?: number | null
  patient_age?: number | null
  nextReservationDate?: string | null
  next_reservation_date?: string | null
  isNewReservation?: boolean
  is_new_reservation?: boolean
  reservationName?: string | null
  reservation_name?: string | null
  callerName?: string | null
  caller_name?: string | null
  messageContent?: string | null
  message_content?: string | null
  status?: CidCallStatus
}) => {
  // const broadcast = useSelector(broadcastSelector)

  // console.debug("액션 데이터 업데이트 수신:", data)

  const dispatch = store.dispatch

  // 서버에서 snake_case로 올 수도 있으므로 변환
  const eventId = data.eventId || data.event_id
  const patientUuid = data.patientUuid || data.patient_uuid
  const encounterUuid = data.encounterUuid || data.encounter_uuid

  switch (data.actionType) {
    case "crm_timeline_changed":
      window.dispatchEvent(new CustomEvent(CRM_TIMELINE_CHANGED_EVENT))
      break
    case "crm_task_changed":
      window.dispatchEvent(new CustomEvent(CRM_TASKS_CHANGED_EVENT))
      break
    case "crm_consultation_changed":
      window.dispatchEvent(new CustomEvent(CRM_CONSULTATIONS_CHANGED_EVENT))
      break
    case "crm_session_changed":
      window.dispatchEvent(new CustomEvent(CRM_SESSIONS_CHANGED_EVENT))
      break
    case "crm_stage_changed":
      window.dispatchEvent(new CustomEvent(CRM_STAGES_CHANGED_EVENT))
      break
    case "kakao_local_cache_changed":
      window.dispatchEvent(new CustomEvent(KAKAO_LOCAL_CACHE_CHANGED_EVENT, { detail: { resourcePath: data.resourcePath } }))
      break
    case "patient_classification_changed":
      window.dispatchEvent(new CustomEvent(PATIENT_CLASSIFICATION_CHANGED_EVENT))
      break
    case "waiting_order_changed":
      window.dispatchEvent(new CustomEvent(WAITING_ORDER_CHANGED_EVENT, { detail: { visitDate: data.visitDate } }))
      break
    case "treatment_room_delta":
      receiveTreatmentRoomDelta(data as unknown as TreatmentRoomSyncPayload)
      break
    case "treatment_room_update":
      // console.debug("SIO 업데이트: treatment_room_update 처리")
      dispatch(
        setBroadcast({
          reference: "treatment_room_update",
          treatmentRoomItems: (data as any).items || [],
        })
      )
      break
    case "treatment_room_settings_changed":
      // console.debug("SIO 업데이트: treatment_room_settings_changed 처리", data)
      dispatch(
        setBroadcast({
          reference: "treatment_room_settings_changed",
        })
      )
      break
    case "treatment_end_alarm_stopped":
      // console.debug("SIO 업데이트: treatment_end_alarm_stopped 처리")
      dispatch(
        setBroadcast({
          reference: "treatment_end_alarm_stopped",
        })
      )
      break
    case "encounter_chuna":
      // 리덕스 추가
      // console.debug("SIO 업데이트: encounter_chuna 처리")
      dispatch(fetchChunaPendingCount())
      break
    case "encounter_herbal":
      // 리덕스 추가
      // console.log("SIO 업데이트: encounter_herbal 처리")
      dispatch(fetchHerbalPendingCount())
      break
    case "nhis_chupyak":
      // console.debug("SIO 업데이트: nhis_chupyak 처리")
      dispatch(fetchNhisChupyakPendingCount())
      break
    case "encounter_changed":
      window.dispatchEvent(new CustomEvent(COLLABORATIVE_NOTE_CHANGED_EVENT, { detail: { encounterUuid, patientUuid } }))
      // console.debug("SIO 업데이트: encounter_changed 처리", { patientUuid, encounterUuid })
      if (patientUuid) {
        if (isLocalEncounterHistoryEvent(eventId, patientUuid)) {
          break
        }
        dispatch(
          setBroadcast({
            reference: "encounter_changed",
            broadcastEventId: eventId || null,
            broadcastedPatientUuid: patientUuid,
            broadcastedEncounterUuid: encounterUuid,
            broadcastedInsuranceUuid: data.insuranceUuid || null,
          })
        )
      }
      break
    case "encounter_package_purchase_changed":
      if (patientUuid) {
        dispatchPackageBroadcastEvent({
          actionType: "encounter_package_purchase_changed",
          eventId: eventId || null,
          patientUuid,
          encounterUuid: encounterUuid || null,
        })
      }
      break
    case "encounter_package_usage_changed":
      if (patientUuid) {
        dispatchPackageBroadcastEvent({
          actionType: "encounter_package_usage_changed",
          eventId: eventId || null,
          patientUuid,
          encounterUuid: encounterUuid || null,
        })
      }
      break

    case "encounter_deleted":
      // console.debug("SIO 업데이트: encounter_deleted 처리", { patientUuid })
      if (patientUuid) {
        dispatch(
          setBroadcast({
            reference: "encounter_deleted",
            broadcastedPatientUuid: patientUuid,
          })
        )
      }
      break
    case "encounter_created":
      // console.debug("SIO 업데이트: encounter_created 처리", { patientUuid })
      if (patientUuid) {
        dispatch(
          setBroadcast({
            reference: "encounter_created",
            broadcastedPatientUuid: patientUuid,
          })
        )
      }
      break

    case "encounter_first_visit_changed":
      // console.debug("SIO 업데이트: encounter_first_visit_changed 처리", { patientUuid, encounterUuid })
      if (encounterUuid) {
        dispatch(
          setBroadcast({
            reference: "encounter_first_visit_changed",
            broadcastedPatientUuid: patientUuid,
            broadcastedEncounterUuid: encounterUuid,
          })
        )
      }
      break
    case "visit_reception_created":
      {
        const state = store.getState() as RootState
        const memberUuid = resolveCurrentMemberUuidFromState(state)
        if (!memberUuid) {
          break
        }

        const settings = getUserFeatureSettings(memberUuid)
        const receptionSource = data.receptionSource || data.reception_source || "general"

        if (receptionSource === "kiosk") {
          if (settings.kioskReceptionAlarmEnabled) {
            playKioskReceptionAlarm(memberUuid)
          }
        } else if (settings.generalReceptionAlarmEnabled) {
          playGeneralReceptionAlarm(memberUuid)
        }
      }
      break
    case "cid_incoming_call": {
      const detail: CidIncomingCallEventDetail = {
        cidHistoryUuid: data.cidHistoryUuid || data.cid_history_uuid || "",
        provider: data.provider === "lguplus" ? "lguplus" : "kt",
        remoteNumber: data.remoteNumber || data.remote_number || "",
        eventDt: data.eventDt ?? data.event_dt ?? null,
        patientUuid: patientUuid || null,
        patientId: data.patientId ?? data.patient_id ?? null,
        patientName: data.patientName || null,
        patientGender: data.patientGender ?? data.patient_gender ?? null,
        patientAge: data.patientAge ?? data.patient_age ?? null,
        nextReservationDate: data.nextReservationDate ?? data.next_reservation_date ?? null,
        isNewReservation: data.isNewReservation ?? data.is_new_reservation ?? false,
        reservationName: data.reservationName ?? data.reservation_name ?? null,
        callerName: data.callerName ?? data.caller_name ?? null,
      }
      window.dispatchEvent(new CustomEvent<CidIncomingCallEventDetail>(CID_INCOMING_CALL_EVENT, { detail }))
      break
    }
    case "cid_incoming_message": {
      const detail: CidIncomingMessageEventDetail = {
        cidHistoryUuid: data.cidHistoryUuid || data.cid_history_uuid || "",
        provider: data.provider === "lguplus" ? "lguplus" : "kt",
        remoteNumber: data.remoteNumber || data.remote_number || "",
        eventDt: data.eventDt ?? data.event_dt ?? null,
        patientUuid: patientUuid || null,
        patientId: data.patientId ?? data.patient_id ?? null,
        patientName: data.patientName || null,
        patientGender: data.patientGender ?? data.patient_gender ?? null,
        patientAge: data.patientAge ?? data.patient_age ?? null,
        nextReservationDate: data.nextReservationDate ?? data.next_reservation_date ?? null,
        messageContent: data.messageContent ?? data.message_content ?? "",
      }
      window.dispatchEvent(new CustomEvent<CidIncomingMessageEventDetail>(CID_INCOMING_MESSAGE_EVENT, { detail }))
      break
    }
    case "cid_call_status_changed": {
      const detail: CidCallStatusChangedEventDetail = {
        cidHistoryUuid: data.cidHistoryUuid || data.cid_history_uuid || "",
        status: data.status || "ended",
      }
      window.dispatchEvent(new CustomEvent<CidCallStatusChangedEventDetail>(CID_CALL_STATUS_CHANGED_EVENT, { detail }))
      break
    }
    //자격조회 필요 브로드캐스트
    case "visit_nhis_check_required":
      // console.debug("SIO 업데이트: visit_nhis_check_required 처리", data)
      await handleVisitNhisCheckRequired({
        visitUuid: data.visitUuid,
        patientUuid,
        insuranceUuid: data.insuranceUuid,
        patientIdentity: data.patientIdentity,
        patientName: data.patientName,
        hospitalCode: data.hospitalCode,
        visitDate: data.visitDate,
        patientVerification: data.patientVerification,
      })
      break
    case "visit_nhis_checked":
      // console.debug("SIO 업데이트: visit_nhis_checked 처리", data)
      if (patientUuid) {
        const state = store.getState() as RootState
        const selectedPatient = state.patient?.selectedPatient

        // console.info("[PatientVerification][Client] visit_nhis_checked 수신", {
        //   patientUuid,
        //   selectedPatientUuid: selectedPatient?.patientUuid,
        //   selectedPatientVerification: selectedPatient?.patientVerification,
        //   payloadPatientVerification: data.patientVerification,
        //   success: data.success,
        //   jobStatus: data.jobStatus,
        // })

        if (selectedPatient?.patientUuid === patientUuid && data.patientVerification && data.success !== false) {
          dispatch(patchSelectedPatient({ patientVerification: true }))
          // console.info("[PatientVerification][Client] visit_nhis_checked로 selectedPatient 갱신", {
          //   patientUuid,
          //   patchedPatientVerification: true,
          // })
        }
      }

      // console.debug("NHIS 완료 브로드캐스트를 수신했습니다.", {
      //   visitUuid: data.visitUuid,
      //   patientUuid,
      //   patientVerification: data.patientVerification,
      // })
      break
    case "uninsured_category_changed":
      // console.debug("SIO 업데이트: uninsured_category_changed 처리")
      dispatch(fetchUninsuredCategory())
      dispatch(fetchUninsuredItems())
      break

    case "uninsured_item_changed":
      // console.debug("SIO 업데이트: uninsured_item_changed 처리")
      dispatch(fetchUninsuredItems())
      break
    case "memo_changed":
      if (patientUuid) {
        dispatch(
          setBroadcast({
            reference: "memo_changed",
            broadcastedPatientUuid: patientUuid,
          })
        )
      }
      break
    case "hospital_setting_changed":
      window.dispatchEvent(new CustomEvent(CRM_SETTINGS_CHANGED_EVENT))
      // 병원 설정 변경 브로드캐스트 수신 → 설정만 재로드
      // console.debug("SIO 업데이트: hospital_setting_changed 처리", data)
      try {
        const settings = await dispatch(fetchHospitalSettings()).unwrap()
        // console.debug(`✅ [SIO] 병원 설정 ${settings.length}개 재로드 완료`)
      } catch (error) {
        console.error("❌ [SIO] 병원 설정 재로드 실패:", error)
      }
      break

    case "hospital_info_changed":
      // 병원 정보 변경 브로드캐스트 수신 → 병원 정보만 재로드
      // console.debug("SIO 업데이트: hospital_info_changed 처리", data)
      try {
        await dispatch(fetchPublicHospitalInfo()).unwrap()
        // console.debug("✅ [SIO] 병원 정보 재로드 완료")
      } catch (error) {
        console.error("❌ [SIO] 병원 정보 재로드 실패:", error)
      }
      break

    case "hospital_stamp_changed":
      // 병원 도장 변경 브로드캐스트 수신 → 공개 도장 재로드
      // console.debug("SIO 업데이트: hospital_stamp_changed 처리", data)
      try {
        await dispatch(fetchPublicStamp()).unwrap()
        // console.debug("✅ [SIO] 병원 도장 재로드 완료")
      } catch (error) {
        console.error("❌ [SIO] 병원 도장 재로드 실패:", error)
      }
      break

    case "member_stamp_changed":
      // 개인 도장 변경 브로드캐스트 수신 → 내 도장 재로드
      // console.debug("SIO 업데이트: member_stamp_changed 처리", data)
      try {
        await dispatch(fetchMyStamp()).unwrap()
        // console.debug("✅ [SIO] 개인 도장 재로드 완료")
      } catch (error) {
        console.error("❌ [SIO] 개인 도장 재로드 실패:", error)
      }
      break

    case "patient_deleted": {
      // console.debug("SIO 업데이트: patient_deleted 처리", { patientUuid })
      if (!patientUuid) {
        break
      }

      const state = store.getState() as RootState
      const selectedPatient = state.patient?.selectedPatient
      const selectedVisit = state.selectedVisit?.visit

      if (selectedPatient?.patientUuid !== patientUuid && selectedVisit?.patientUuid !== patientUuid) {
        break
      }

      dispatch(clearSelectedPatient())
      dispatch(clearSelectedVisit())
      dispatch(clearSelectedVisitUuid())
      // console.debug("✅ [SIO] 삭제된 환자 선택 상태 정리 완료", { patientUuid })
      break
    }

    case "patient_changed": {
      // console.debug("SIO 업데이트: patient_changed 처리", { patientUuid })
      if (!patientUuid) {
        break
      }

      dispatch(
        setBroadcast({
          reference: "patient_changed",
          broadcastedPatientUuid: patientUuid,
        })
      )

      const state = store.getState() as RootState
      const selectedPatient = state.patient?.selectedPatient
      if (selectedPatient?.patientUuid !== patientUuid) {
        break
      }

      try {
        const insuranceUuid = selectedPatient?.insuranceUuid
        await dispatch(fetchPatient(insuranceUuid ? { patientUuid, insuranceUuid } : patientUuid)).unwrap()
        // console.debug("✅ [SIO] 선택 환자 정보 재로드 완료", { patientUuid })
      } catch (error) {
        console.error("❌ [SIO] 선택 환자 정보 재로드 실패:", error)
      }
      break
    }

    case "patient_consent_changed": {
      // console.debug("SIO 업데이트: patient_consent_changed 처리", { patientUuid })
      if (!patientUuid) {
        break
      }

      const state = store.getState() as RootState
      const selectedPatient = state.patient?.selectedPatient
      if (selectedPatient?.patientUuid !== patientUuid) {
        break
      }

      try {
        const insuranceUuid = selectedPatient?.insuranceUuid
        await dispatch(fetchPatient(insuranceUuid ? { patientUuid, insuranceUuid } : patientUuid)).unwrap()
        // console.debug("✅ [SIO] 환자 동의서 정보 재로드 완료", { patientUuid })
      } catch (error) {
        console.error("❌ [SIO] 환자 동의서 정보 재로드 실패:", error)
      }
      break
    }

    default:
      console.warn("알 수 없는 액션 타입:", data.actionType)
      break
  }
}

const handleVisitNhisCheckRequired = async ({
  visitUuid,
  patientUuid,
  insuranceUuid,
  patientIdentity,
  patientName,
  hospitalCode,
  visitDate,
  patientVerification,
}: VisitNhisPayload) => {
  if (!visitUuid) {
    console.warn("NHIS 브로드캐스트: visitUuid 없음", { patientUuid })
    return
  }

  // store.dispatch(
  //   setBroadcast({
  //     reference: "visit_nhis_check_required",
  //     broadcastedVisitUuid: visitUuid,
  //     broadcastedPatientUuid: patientUuid,
  //     broadcastedInsuranceUuid: insuranceUuid,
  //   })
  // )
  // 인증서 자동인증 기능 비활성화:
  // 키오스크 접수 브로드캐스트 수신 시 저장된 인증서 비밀번호를 이용해
  // NHIS 자격조회를 자동 실행하던 흐름을 중단한다.
  // console.info("NHIS 자동 자격조회 비활성화: 인증서 자동인증 기능이 제거되었습니다.", { visitUuid, patientUuid })
}

interface VisitNhisPayload {
  visitUuid?: string
  patientUuid?: string
  insuranceUuid?: string
  patientIdentity?: string
  patientName?: string
  hospitalCode?: string
  visitDate?: string
  patientVerification?: boolean
}
